<script lang="ts">
  import { marked } from "marked";
  import type { Snippet } from "svelte";

  interface Props {
    source: string;
    onSourceClick?: (ids: {
      refId: number;
      refIndex: number;
      webSourceId?: string;
    }) => void;
    children?: Snippet;
  }
  let { source, onSourceClick, children }: Props = $props();

  const html = $derived(marked(source, { gfm: true }));

  function handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    const sourceRef = target.closest(".source-ref");
    const webSourceRef = target.closest(".web-source-ref");

    if (sourceRef) {
      const refId = +sourceRef.getAttribute("data-ref-id");
      const refIndex = +sourceRef.getAttribute("data-ref-index");
      onSourceClick?.({ refId, refIndex });
      event.preventDefault();
    } else if (webSourceRef) {
      const webSourceId = webSourceRef.getAttribute("data-web-source-id");
      onSourceClick?.({ refId: 0, refIndex: 0, webSourceId });
      event.preventDefault();
    }
  }
</script>

<div class="markdown" role="presentation" onclick={handleClick}>
  {@html html}{@render children?.()}
</div>

<style lang="scss">
  @use "@astlai/common/styles/mixins" as lib;

  :global {
    .markdown {
      div.source-ref,
      div.web-source-ref {
        cursor: pointer;
        font-size: 0.75em;
        line-height: 0.75rem;
        background: var(--surface-overlay-subtle);
        box-shadow: inset 0 0 0 1px var(--border-subtle);
        padding: 0.25rem 0.375rem 0.25rem 0.25rem;
        border-radius: 0.5rem;
        display: inline-flex;
        flex-direction: row;
        @include lib.flex-align();
        gap: 0.25rem;
      }
      div.web-source-ref {
        border-radius: 1rem;
        svg {
          width: 1em;
          height: 1em;
          vertical-align: middle;
        }
      }

      span.highlight {
        padding: 4px 0px;
      }

      & > * {
        &:first-child {
          margin-top: 0;
        }
        &:last-child {
          margin-bottom: 0;
        }
      }

      h1 {
        font-size: 2em;
        margin: 0.67em 0;
      }
      h2 {
        font-size: 1.5em;
        margin: 0.75em 0;
      }
      h3 {
        font-size: 1.17em;
        margin: 0.83em 0;
      }
      h4,
      p,
      ul,
      fieldset,
      form,
      ol,
      dl,
      dir,
      menu {
        margin: 1.12em 0;
      }
      blockquote {
        padding: 0.5rem 1rem;
      }
      h5 {
        font-size: 0.83em;
        margin: 1.5em 0;
      }
      h6 {
        font-size: 0.75em;
        margin: 1.67em 0;
      }
      img {
        max-width: 100%;
        border-radius: 0.5rem;
      }
      a {
        text-decoration: underline;
      }
      table {
        display: block;
        overflow-x: auto;
        border-spacing: 0;
        border-collapse: collapse;
        margin: 1rem 0;
        max-width: 100%;

        th,
        td {
          &:first-child {
            padding-left: 0;
          }
        }

        th {
          text-align: left;
          border-bottom: 2px solid var(--border-subtle);
          padding: 0.5rem;
          white-space: nowrap;
        }

        td {
          padding: 0.25rem 0.5rem;
        }

        tr:not(:last-child) td {
          border-bottom: 1px solid var(--border-subtle);
        }
      }
    }
  }
</style>
